/**
 * uniapp BE axios client (V2). Used by the V2 DAT pages that call the
 * uniapp-backend DAT endpoints (ported from n8n in Phase 1).
 *
 * Why a separate client (not the existing `axiosClient.js`):
 *   - `axiosClient` already targets `REACT_APP_API_URL` (uniapp prod
 *     `https://api.uniapp.com`). We want V2 to point at staging
 *     (`https://staging-api.uniapp.vn`) during the migration trial.
 *   - V2 endpoints return uniapp's `ResponseHandler` envelope
 *     `{ message, data, pagination }`. Auto-unwrap `data` so call
 *     sites don't have to remember `.data.data`.
 *   - When migration cuts over, just change baseURL — no callsite churn.
 *
 * Env vars:
 *   - REACT_APP_UNIAPP_API_URL — base URL for V2 endpoints
 *       dev:  http://localhost:5050
 *       staging trial: https://staging-api.uniapp.vn
 *       cutover (later): https://api.uniapp.vn (or stay on staging until verified)
 *
 * Falls back to `https://staging-api.uniapp.vn` if env var unset so V2
 * pages still work in a Vercel preview without env config.
 */
import axios from 'axios';
import { getToken, clearAuth } from '../utils/auth';

const baseURL =
  process.env.REACT_APP_UNIAPP_API_URL ||
  'https://staging-api.uniapp.vn';

const uniappClient = axios.create({ baseURL });

// Auto-unwrap uniapp's ResponseHandler envelope.
// Original shape: { message, data, pagination }
// Returns: { data, pagination, message } at top level so callers can
//   destructure consistently. .data is the actual payload (array / doc /
//   plain object depending on endpoint).
const handleResponse = (res) => {
  const body = res.data;
  // Endpoint trả file (blob) không có envelope JSON — trả nguyên blob kèm
  // vài header FE cần (tên file, số liệu) vì interceptor nuốt mất `res`.
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    const cd = res.headers?.['content-disposition'] || '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    return {
      data: body,
      _headers: {
        filename: m ? decodeURIComponent(m[1]) : '',
        studentCount: res.headers?.['x-student-count'],
        portraitCount: res.headers?.['x-portrait-count'],
        systemCount: res.headers?.['x-system-count'],
        // Tổng hợp DAT: bao nhiêu học viên trong file tìm được phiên, và
        // tổng số phiên đã cộng. Không có hai số này thì người dùng không
        // biết file ra trống là do chọn nhầm file hay do chưa ai học buổi nào.
        matchedCount: res.headers?.['x-matched-count'],
        sessionCount: res.headers?.['x-session-count'],
      },
    };
  }
  if (body && typeof body === 'object' && 'data' in body) {
    return {
      data: body.data,
      pagination: body.pagination,
      message: body.message,
      _raw: body,
    };
  }
  return { data: body, _raw: body };
};

const handleError = (err) => {
  if (err?.response?.status === 401) clearAuth();
  return Promise.reject(err);
};

uniappClient.interceptors.request.use(
  (req) => {
    // Reuse the same JWT util as the V1 axiosClient. Phase 3 will
    // switch this to a uniapp-issued JWT (vs the legacy n8n token).
    const token = getToken();
    if (token) req.headers.Authorization = `Bearer ${token}`;
    return req;
  },
  (err) => Promise.reject(err)
);

uniappClient.interceptors.response.use(handleResponse, handleError);

export default uniappClient;
